import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase, Typography } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { CustomVacantButton } from '../button';
import CorporateCardDialog from './CorporateCardDialog';
import { cardSaveAPI, cardDeleteAPI, mileageAPI } from '../../api/login';
import tokens from '../theme/tokens';

// 마이페이지 기업회원 카드 — 사업자번호 · 정산일 · 거래처 할인률 · 마일리지(원장) · 법인카드 · [운임 조회]
const useStyles = makeStyles(() => ({
  card: {
    width: '100%',
    padding: '22px 24px',
    borderRadius: 16,
    backgroundColor: tokens.surface,
    boxShadow: tokens.cardShadow,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
    '& .MuiTypography-root': { fontWeight: 700, color: tokens.ink, fontSize: 15 },
  },
  tag: {
    height: 22, padding: '0 8px', borderRadius: 11, backgroundColor: tokens.primarySoft, color: tokens.primaryDark, fontSize: 12, fontWeight: 700, lineHeight: '22px',
  },
  rows: {
    display: 'grid',
    gridTemplateColumns: '88px 1fr',
    rowGap: 10,
    columnGap: 12,
    fontSize: 15,
    letterSpacing: -0.5,
    '& small': { color: tokens.muted, fontSize: 14 },
    '& b': { color: tokens.ink, fontWeight: 500 },
  },
  mileage: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginTop: 16,
    padding: '14px 16px',
    borderRadius: 12,
    backgroundColor: tokens.primarySoft,
    '& small': { fontSize: 13, color: tokens.muted },
    '& b': { fontSize: 22, fontWeight: 700, color: tokens.primaryDark, letterSpacing: -1 },
    '& b span': { fontSize: 13, marginLeft: 2, color: tokens.text, fontWeight: 500 },
  },
  logs: {
    marginTop: 8,
    borderTop: `1px solid ${tokens.lineSoft}`,
    '& div': { display: 'flex', justifyContent: 'space-between', padding: '8px 2px', fontSize: 13, borderBottom: `1px solid ${tokens.lineSoft}`, color: tokens.text },
    '& div span:last-child': { fontWeight: 700, color: tokens.ink },
  },
  more: { marginTop: 6, fontSize: 13, color: tokens.primaryDark },
  actions: { display: 'flex', gap: 10, marginTop: 14, '& > *': { flex: '1 1 0', height: 46, fontSize: 15 } },
  cardLine: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  del: { fontSize: 13, color: tokens.muted, '&:hover': { color: tokens.primaryDark } },
}));

const fmtBizNo = (v) => String(v || '').replace(/^(\d{3})(\d{2})(\d{5})$/, '$1-$2-$3');
const fmtDate = (v) => { const d = new Date(v); return isNaN(d.getTime()) ? '' : `${d.getMonth() + 1}/${d.getDate()}`; };
const TYPE = { earn: '적립', use: '사용', adjust: '조정' };

function CorporateInfoCard({ user, onUserChange }) {
  const cs = useStyles();
  const history = useHistory();
  const [cardOpen, setCardOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const biz = (user && user.biz) || {};

  useEffect(() => {
    mileageAPI().then((resp) => { if (resp && resp.status === 200) setLogs(resp.data.logs || []); });
  }, [user && user.mileage]);

  const saveCard = async (data) => {
    const resp = await cardSaveAPI(data);
    if (!resp || resp.status !== 200) return { ok: false, message: resp && resp.data && resp.data.message };
    onUserChange(resp.data.user);
    return { ok: true };
  };
  const deleteCard = async () => {
    if (!window.confirm('등록된 법인카드를 삭제할까요?')) return;
    const resp = await cardDeleteAPI();
    if (resp && resp.status === 200) onUserChange(resp.data.user);
  };

  return (
    <div className={cs.card}>
      <CorporateCardDialog open={cardOpen} onClose={() => setCardOpen(false)} onSave={saveCard} />
      <div className={cs.head}>
        <Typography>기업회원 정보</Typography>
        <span className={cs.tag}>정산일 {biz.settleLabel || '말일'}</span>
      </div>
      <div className={cs.rows}>
        <small>사업자번호</small><b>{fmtBizNo(biz.bizNo) || '-'}</b>
        <small>거래처</small><b>{biz.clientName || user.companyName || '-'}</b>
        <small>할인률</small><b>{biz.discountRate ? `${biz.discountRate}%` : '없음'}</b>
        <small>법인카드</small>
        <b className={cs.cardLine}>
          <span>{biz.card ? `${biz.card.company} **** ${biz.card.last4} (${biz.card.expMonth}/${biz.card.expYear})` : '미등록'}</span>
          {biz.card && <ButtonBase className={cs.del} onClick={deleteCard} focusRipple={false}>삭제</ButtonBase>}
        </b>
      </div>
      <div className={cs.mileage}>
        <small>마일리지</small>
        <b>{Number(user.mileage || 0).toLocaleString('ko-KR')}<span>P</span></b>
      </div>
      {logs.length > 0 && (
        <React.Fragment>
          <ButtonBase className={cs.more} onClick={() => setShowLogs((v) => !v)} focusRipple={false}>{showLogs ? '내역 접기' : `내역 ${logs.length}건 보기`}</ButtonBase>
          {showLogs && (
            <div className={cs.logs}>
              {logs.map((l) => (
                <div key={l.id}>
                  <span>{fmtDate(l.at)} · {TYPE[l.type] || l.type}{l.memo ? ` · ${l.memo}` : ''}{l.orderNumber ? ` · ${l.orderNumber}` : ''}</span>
                  <span>{l.amount > 0 ? '+' : ''}{Number(l.amount).toLocaleString('ko-KR')}</span>
                </div>
              ))}
            </div>
          )}
        </React.Fragment>
      )}
      <div className={cs.actions}>
        <CustomVacantButton variant="contained" color="primary" onClick={() => setCardOpen(true)}>{biz.card ? '법인카드 변경' : '법인카드 등록'}</CustomVacantButton>
        <CustomVacantButton variant="contained" color="primary" onClick={() => history.push('/fare')}>운임 조회</CustomVacantButton>
      </div>
    </div>
  );
}

export default CorporateInfoCard;
